import { useState, useCallback } from "react"
import { router, usePage } from "@inertiajs/react"
import { format } from "date-fns"
import Layout from "@/pages/layouts/application"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Avatar } from "@/components/ui/avatar"
import { ChevronLeft, Send, Plus, X as CloseIcon } from "lucide-react"
import postsApi from "@/api/Organizations/PostsApi"
import contentApi from "@/api/Organizations/ContentApi"
import * as api from "@/api"
import TwitterThreadEditor from "./components/twitter_thread_editor"
import LinkedinThreadEditor from "./components/linkedin_thread_editor"
import { Card, CardContent } from "@/components/ui/card"
import { DateTimePicker } from "@/components/ui/datepicker"
import XIcon from "@/assets/icons/x.svg?react"
import LinkedinIcon from "@/assets/icons/linkedin.svg?react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import { useThread } from "./hooks/thread"

const cx = (...classes) => classes.filter(Boolean).join(" ")

export default function Show({ post: initialPost }) {
  const { organization, user } = usePage().props.auth
  const { integrations } = usePage().props
  
  const twitterProfile = integrations?.twitter
  const linkedinProfile = integrations?.linkedin
  const hasConnectedSocials = integrations?.twitter?.connected || integrations?.linkedin?.connected

  const { 
    posts: twitterPosts, 
    updatePost: updateTwitterPost, 
    addPost: addTwitterPost, 
    removePost: removeTwitterPost, 
    joinedContent: twitterJoinedContent 
  } = useThread(initialPost.twitter_content || initialPost.content)

  const { 
    posts: linkedinPosts, 
    updatePost: updateLinkedinPost, 
    addPost: addLinkedinPost, 
    removePost: removeLinkedinPost, 
    joinedContent: linkedinJoinedContent 
  } = useThread(initialPost.linkedin_content || initialPost.content)

  // Profile display data
  const twitterDisplayName = twitterProfile?.display_name || twitterProfile?.handle || organization?.name || "Your company"
  const twitterHandleLabel = twitterProfile?.handle || (user?.email ? user.email.split("@")[0] : "founder")
  const twitterAvatarUrl = twitterProfile?.avatar_url
  const twitterVerified = twitterProfile?.verified || false

  const linkedinDisplayName = linkedinProfile?.display_name || linkedinProfile?.handle || organization?.name || "Your company"
  const linkedinHandleLabel = linkedinProfile?.handle || (user?.email ? user.email.split("@")[0] : "founder")
  const linkedinAvatarUrl = linkedinProfile?.avatar_url

  const twitterEnabled = !!integrations?.twitter?.connected
  const linkedinEnabled = !!integrations?.linkedin?.connected
  const platformKeys = ["twitter", "linkedin"]

  const [newComment, setNewComment] = useState("")
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState(initialPost.asset?.file_url || null)
  const [mediaType, setMediaType] = useState(initialPost.asset?.file_type?.startsWith('video') ? 'video' : 'image')
  const [isSaving, setIsSaving] = useState(false)
  const [showPublishDialog, setShowPublishDialog] = useState(false)
  const [showUnscheduleDialog, setShowUnscheduleDialog] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)
  const [rescheduleAt, setRescheduleAt] = useState(() => {
    if (initialPost.status === "draft") return null
    return initialPost.scheduled_at ? new Date(initialPost.scheduled_at) : null
  })

  const [targets, setTargets] = useState(() => {
    if (initialPost.targets?.length) {
      return {
        twitter: initialPost.targets.includes("twitter"),
        linkedin: initialPost.targets.includes("linkedin"),
      }
    }
    // With no connected account, still surface the draft for review by
    // defaulting to the LinkedIn preview (publishing stays gated below).
    if (!integrations?.twitter?.connected && !integrations?.linkedin?.connected) {
      return { twitter: false, linkedin: true }
    }
    return {
      twitter: !!integrations?.twitter?.connected,
      linkedin: !!integrations?.linkedin?.connected,
    }
  })
  const [selectionOrder, setSelectionOrder] = useState(() => {
    if (initialPost.targets?.length) {
      return initialPost.targets.filter(t => ["twitter", "linkedin"].includes(t))
    }
    const order = []
    if (integrations?.twitter?.connected) order.push("twitter")
    if (integrations?.linkedin?.connected) order.push("linkedin")
    return order
  })
  const [activePreview, setActivePreview] = useState(() => {
    if (initialPost.targets?.includes("twitter")) return "twitter"
    if (initialPost.targets?.includes("linkedin")) return "linkedin"
    if (integrations?.twitter?.connected) return "twitter"
    if (integrations?.linkedin?.connected) return "linkedin"
    // No connected account: still show the LinkedIn draft for review.
    return "linkedin"
  })

  // Check if we just published (either from URL param or published_at is very recent)
  const urlParams = new URLSearchParams(window.location.search)
  const justPublished = urlParams.get('published') === 'true'
  const [isPublishing, setIsPublishing] = useState(justPublished)

  const isPublished = !!initialPost.published_at || isPublishing

  const selectedTargets = Object.entries(targets)
    .filter(([_, value]) => value)
    .map(([key]) => key)

  const hasSelectedTargets = selectedTargets.length > 0

  const selectedPlatformsInOrder = selectionOrder.filter((platform) => targets[platform])
  const selectedNotTracked = platformKeys.filter(
    (platform) => targets[platform] && !selectedPlatformsInOrder.includes(platform)
  )
  const unselectedPlatforms = platformKeys.filter((platform) => !targets[platform])
  const pillOrder = [...selectedPlatformsInOrder, ...selectedNotTracked, ...unselectedPlatforms]

  const selectPreview = (key, enabled) => {
    if (!enabled || isPublished) return
    if (!targets[key]) {
      setTargets((prev) => ({ ...prev, [key]: true }))
      setSelectionOrder((prev) => (prev.includes(key) ? prev : [...prev, key]))
    }
    setActivePreview(key)
  }

  const toggleTarget = (key, enabled) => {
    if (!enabled || isPublished) return

    const wasSelected = targets[key]
    const nextTargets = { ...targets, [key]: !wasSelected }
    const nextSelectionOrder = wasSelected
      ? selectionOrder.filter((platform) => platform !== key)
      : selectionOrder.includes(key)
        ? selectionOrder
        : [...selectionOrder, key]

    setTargets(nextTargets)
    setSelectionOrder(nextSelectionOrder)

    if (wasSelected) {
      if (activePreview === key) {
        setActivePreview(nextSelectionOrder[0] || null)
      }
      return
    }

    setActivePreview(key)
  }

  const handleSaveStart = useCallback(() => {
    setIsSaving(true)
  }, [])

  const handleSaveEnd = useCallback(() => {
    setIsSaving(false)
  }, [])

  const handleAddComment = () => {
    if (!newComment.trim()) return

    router.patch(
      postsApi.update.path({ org_nano_id: organization.nano_id, id: initialPost.id }),
      {
        post: {
          comments_attributes: [{ content: newComment }]
        }
      },
      {
        preserveScroll: true,
        onSuccess: () => setNewComment("")
      }
    )
  }

  const handleUnschedule = () => {
    router.patch(
      postsApi.update.path({ org_nano_id: organization.nano_id, id: initialPost.id }),
      { post: { status: "draft" } }
    )
  }

  const handleSchedule = () => {
    if (!rescheduleAt) return

    router.patch(
      postsApi.update.path({ org_nano_id: organization.nano_id, id: initialPost.id }),
      { post: { status: "approved", scheduled_at: rescheduleAt.toISOString(), targets: selectedTargets } }
    )
  }

  const handlePublishNow = () => {
    if (!organization.subscribed && organization.free_posts_remaining <= 0) {
      router.visit(api.organizationsUpgrade.show.path({ org_nano_id: organization.nano_id }))
      return
    }
    setIsPublishing(true)
    router.post(
      api.organizationsPostsPublish.create.path({ org_nano_id: organization.nano_id, post_id: initialPost.id }),
      { targets: selectedTargets }
    )
  }

  const handleMediaChange = (mediaData, previewUrl, newMediaType) => {
    setMediaPreviewUrl(previewUrl)
    setMediaType(newMediaType)

    let payload = {}
    if (mediaData?.file) {
      payload = { asset_attributes: { file: mediaData.file } }
    } else if (mediaData?.asset_id) {
      payload = { asset_attachment_attributes: { asset_id: mediaData.asset_id } }
    } else {
      payload = { asset_attachment_attributes: { _destroy: true } }
    }

    handleSaveStart()
    router.patch(
      postsApi.update.path({ org_nano_id: organization.nano_id, id: initialPost.id }),
      { post: payload },
      { preserveScroll: true, preserveState: true, onFinish: handleSaveEnd }
    )
  }

  const currentMediaSelection = mediaPreviewUrl
    ? { type: 'existing', id: initialPost.asset?.id, url: mediaPreviewUrl, mediaType }
    : null

  return (
    <Layout>
      <div className="space-y-6 pb-24">
        {/* Minimal header */}
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => router.get(contentApi.show.path({ org_nano_id: organization.nano_id }))}
          >
            Back to Calendar
          </Button>
        </div>

        <div className="space-y-1">
          {/* Inline control bar */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {hasSelectedTargets && (
                <div className="flex items-center gap-1 rounded-md bg-background px-2 py-1 shadow-sm">
                  {pillOrder.map((platform) => {
                    const isTwitter = platform === "twitter"
                    const enabled = isTwitter ? twitterEnabled : linkedinEnabled
                    const selected = targets[platform]
                    const active = activePreview === platform
                    const displayName = isTwitter ? twitterDisplayName : linkedinDisplayName
                    const selectedIconSize = isTwitter ? "h-3 w-3" : "h-3.5 w-3.5"
                    const unselectedIconSize = "h-3.5 w-3.5"

                    return (
                      <div
                        key={platform}
                        className={cx(
                          "group flex items-center rounded-lg px-0.5 transition-colors duration-200",
                          selected && active
                            ? "bg-foreground text-background hover:bg-foreground/90"
                            : selected
                              ? "text-foreground hover:bg-muted/60"
                              : "bg-transparent text-muted-foreground hover:bg-muted/40",
                          !enabled && "cursor-not-allowed opacity-50",
                          isPublished && "pointer-events-none"
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => selectPreview(platform, enabled)}
                          disabled={!enabled || isPublished}
                          className={cx(
                            "flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition",
                            selected && !active && "text-foreground"
                          )}
                        >
                          {selected ? (
                            isTwitter ? (
                              <XIcon className={`${selectedIconSize} fill-current`} />
                            ) : (
                              <LinkedinIcon className={`${selectedIconSize} fill-current`} />
                            )
                          ) : (
                            <span className={`relative block ${unselectedIconSize}`}>
                              <Plus className={`${unselectedIconSize} absolute inset-0 opacity-100 transition-opacity duration-200 ease-in-out group-hover:opacity-0`} />
                              {isTwitter ? (
                                <XIcon className={`${unselectedIconSize} absolute inset-0 fill-current opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100`} />
                              ) : (
                                <LinkedinIcon className={`${unselectedIconSize} absolute inset-0 fill-current opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100`} />
                              )}
                            </span>
                          )}

                          {selected ? (
                            <span className="max-w-32 truncate text-[11px] font-medium">
                              {displayName}
                            </span>
                          ) : (
                            <span className="max-w-0 overflow-hidden whitespace-nowrap text-[11px] opacity-0 transition-all duration-200 group-hover:max-w-32 group-hover:opacity-100">
                              {displayName}
                            </span>
                          )}
                        </button>

                        {selected && !isPublished && (
                          <button
                            type="button"
                            onClick={() => toggleTarget(platform, enabled)}
                            disabled={!enabled}
                            className="rounded-full p-0.5 transition hover:bg-background/20"
                            aria-label={isTwitter ? "Remove X target" : "Remove LinkedIn target"}
                          >
                            <CloseIcon className="h-2.5 w-2.5" />
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              <Badge className={isPublished ? "bg-green-100 text-green-700" : initialPost.status === "approved" ? "bg-green-100 text-green-700" : initialPost.status === "rejected" ? "bg-destructive text-destructive-foreground" : "bg-secondary text-secondary-foreground"}>
                {isPublished ? "Published" : initialPost.status === "approved" ? "Scheduled" : initialPost.status.charAt(0).toUpperCase() + initialPost.status.slice(1)}
              </Badge>
              {isSaving && (
                <span className="text-sm text-muted-foreground">Saving...</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Action buttons */}
              {hasSelectedTargets && !isPublished && hasConnectedSocials && (
                <div className="flex items-center gap-2">
                  {initialPost.status === "approved" ? (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="rounded-sm text-muted-foreground hover:text-foreground"
                        onClick={() => setShowUnscheduleDialog(true)}
                      >
                        Unschedule
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="rounded-sm"
                        onClick={() => setShowPublishDialog(true)}
                      >
                        Publish
                      </Button>
                    </>
                  ) : showSchedule ? (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowSchedule(false)}
                        className="rounded-sm text-muted-foreground hover:text-foreground"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleSchedule}
                        className="rounded-sm bg-amber-500 hover:bg-amber-600"
                        disabled={!hasSelectedTargets || !rescheduleAt}
                      >
                        Confirm Schedule
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowSchedule(true)}
                        className="rounded-sm text-muted-foreground hover:text-foreground"
                      >
                        Schedule
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="rounded-sm"
                        onClick={() => setShowPublishDialog(true)}
                      >
                        Publish
                      </Button>
                    </>
                  )}
                </div>
              )}

              {!hasConnectedSocials && !isPublished && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => window.location.href = `/auth/late/connect/linkedin?return_to=/${organization.nano_id}/posts/${initialPost.id}`}
                >
                  Connect LinkedIn to publish
                </Button>
              )}

            </div>
          </div>

          {/* Schedule picker (collapsible) */}
          <div
            className={cx(
                "grid transition-all duration-200",
                showSchedule ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              )}
          >
            <div className="overflow-hidden">
              <div className="rounded-xl border-0 bg-background pb-2">
                <DateTimePicker value={rescheduleAt} onChange={setRescheduleAt} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              {activePreview === "twitter" && targets.twitter && (
                <Card className="rounded-2xl shadow-sm">
                  <CardContent className="p-5">
                    <TwitterThreadEditor
                      posts={twitterPosts}
                      onUpdatePost={updateTwitterPost}
                      onAddPost={addTwitterPost}
                      onRemovePost={removeTwitterPost}
                      joinedContent={twitterJoinedContent}
                      initialContent={initialPost.twitter_content || initialPost.content}
                      saveUrl={postsApi.update.path({ org_nano_id: organization.nano_id, id: initialPost.id })}
                      onSaveStart={handleSaveStart}
                      onSaveEnd={handleSaveEnd}
                      displayName={twitterDisplayName}
                      handleLabel={twitterHandleLabel}
                      avatarUrl={twitterAvatarUrl}
                      verified={twitterVerified}
                      disabled={isPublished}
                      mediaSelection={currentMediaSelection}
                      onMediaChange={handleMediaChange}
                    />
                  </CardContent>
                </Card>
              )}

              {activePreview === "linkedin" && targets.linkedin && (
                <Card className="rounded-2xl shadow-sm">
                  <CardContent className="p-5">
                    <LinkedinThreadEditor
                      posts={linkedinPosts}
                      onUpdatePost={updateLinkedinPost}
                      onAddPost={addLinkedinPost}
                      onRemovePost={removeLinkedinPost}
                      joinedContent={linkedinJoinedContent}
                      initialContent={initialPost.linkedin_content || initialPost.content}
                      initialReplyContent={initialPost.reply?.content || ""}
                      replyId={initialPost.reply?.id || null}
                      saveUrl={postsApi.update.path({ org_nano_id: organization.nano_id, id: initialPost.id })}
                      onSaveStart={handleSaveStart}
                      onSaveEnd={handleSaveEnd}
                      displayName={linkedinDisplayName}
                      handleLabel={linkedinHandleLabel}
                      avatarUrl={linkedinAvatarUrl}
                      disabled={isPublished}
                      mediaSelection={currentMediaSelection}
                      onMediaChange={handleMediaChange}
                      organizationId={organization?.id}
                    />
                  </CardContent>
                </Card>
              )}

              {!hasSelectedTargets && (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground text-center">
                    Select at least one platform to continue.
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => selectPreview("twitter", twitterEnabled)}
                      disabled={!twitterEnabled}
                      className={cx(
                        "inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1.5 text-xs font-medium transition",
                        twitterEnabled
                          ? "text-foreground hover:bg-muted/60"
                          : "cursor-not-allowed text-muted-foreground opacity-50"
                      )}
                    >
                      <XIcon className="h-3 w-3 fill-current" />
                      {twitterDisplayName}
                    </button>
                    <button
                      type="button"
                      onClick={() => selectPreview("linkedin", linkedinEnabled)}
                      disabled={!linkedinEnabled}
                      className={cx(
                        "inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1.5 text-xs font-medium transition",
                        linkedinEnabled
                          ? "text-foreground hover:bg-muted/60"
                          : "cursor-not-allowed text-muted-foreground opacity-50"
                      )}
                    >
                      <LinkedinIcon className="h-3.5 w-3.5 fill-current" />
                      {linkedinDisplayName}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Comments sidebar */}
            <div className="space-y-6">
              <div className="rounded-xl border p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-muted-foreground">Comments</h3>
                </div>
                <div className="mt-4 space-y-4">
                  <div className="space-y-4">
                    {initialPost.comments?.map((comment) => (
                      <div key={comment.id} className="rounded-lg bg-muted/50 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Avatar className="h-6 w-6 text-xs">
                              {linkedinProfile?.avatar_url || twitterProfile?.avatar_url ? (
                                <img 
                                  src={linkedinProfile?.avatar_url || twitterProfile?.avatar_url}
                                  alt="Profile"
                                  className="h-full w-full rounded-full object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center rounded-full bg-muted">
                                  {comment.author?.first_name?.[0] || comment.author?.last_name?.[0] || 
                                    (linkedinProfile?.display_name?.[0] || twitterProfile?.display_name?.[0] || 'A')}
                                </div>
                              )}  
                            </Avatar>
                            <span className="text-sm font-medium">
                              {comment.author?.first_name || comment.author?.last_name 
                                ? `${comment.author.first_name} ${comment.author.last_name}` 
                                : (linkedinProfile?.display_name || twitterProfile?.display_name || 'Anonymous')
                              }
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(comment.created_at), "MMM d, h:mm a")}
                          </span>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Leave a comment..."
                      className="min-h-0 border-border"
                    />
                    <div className="flex justify-end">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                      >
                        Send
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Publish Dialog */}
        <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Publish Now</DialogTitle>
              <DialogDescription>
                Are you sure you want to publish this post instantly?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setShowPublishDialog(false)}
              >
                Reconsider
              </Button>
              <Button
                onClick={() => {
                  handlePublishNow()
                  setShowPublishDialog(false)
                }}
              >
                Publish
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Unschedule Dialog */}
        <Dialog open={showUnscheduleDialog} onOpenChange={setShowUnscheduleDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Unschedule Post</DialogTitle>
              <DialogDescription>
                Are you sure you want to unschedule this post?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setShowUnscheduleDialog(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  handleUnschedule()
                  setShowUnscheduleDialog(false)
                }}
              >
                Unschedule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  )
}
